import React from "react";
import { FormattedMessage } from "react-intl";
import {
  Box,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useColorModeValue,
  useDisclosure,
  Tooltip,
} from "@chakra-ui/react";
import { BiPencil, BiTrash } from "react-icons/bi";

import { MoreOptionsIcon } from "components/Icons";

import RenameBankAccountModal from "./RenameBankAccountModal";
import RemoveBankAccountModal from "./RemoveBankAccountModal";

/**
 * BankMoreOptionsMenu - Component to display further options for a single bank account and handle the selection of each option.
 * @param {BankMoreOptionsMenuProps} props
 * @param {Object} props.bankAccount - the bank account that this component is responsible for.
 * @param {Function} props.onComplete - callback function when an action is performed successfully.
 * @param {Boolean} [props.disableDelete=false] - optionally disable the ability to delete the bank account
 * @param {Boolean} [props.disableDeleteTooltipMessage] - optionally show a tooltip message over delete button if disabled
 * @returns JSX.Element
 */
const MoreOptionsMenu = ({
  bankAccount,
  onComplete,
  disableDelete = false,
  disableDeleteTooltipMessage,
}) => {
  const menuBtnHoverBgColor = useColorModeValue(
    "rgba(226, 232, 240, 1)",
    "rgba(255, 255, 255, 0.08)",
  );

  const removeMenuColor = useColorModeValue("#E53E3E", "#FC8181"); // red.300 and red.500 (same as error colors elsewhere)
  const { isOpen: isRenameOpen, onOpen: onRenameOpen, onClose: onRenameClose } = useDisclosure();
  const { isOpen: isRemoveOpen, onOpen: onRemoveOpen, onClose: onRemoveClose } = useDisclosure();

  return (
    <Box>
      <Menu>
        <MenuButton
          borderRadius="100"
          _hover={{
            background: menuBtnHoverBgColor,
          }}
          onClick={(e) => e.stopPropagation()}
          type="button"
          aria-label={`Bank Account Options for ${bankAccount.bank_account_id}`}
        >
          <MoreOptionsIcon />
        </MenuButton>
        <MenuList>
          <MenuItem
            icon={<BiPencil />}
            onClick={(e) => {
              e.stopPropagation();
              onRenameOpen();
            }}
          >
            <FormattedMessage defaultMessage="Rename" description="button label" />
          </MenuItem>
          <Tooltip
            isDisabled={!disableDelete && disableDeleteTooltipMessage}
            label={disableDeleteTooltipMessage}
            placement="bottom-start"
            backgroundColor="lightBackground"
            color="secondaryText"
          >
            <MenuItem
              color={removeMenuColor}
              icon={<BiTrash />}
              onClick={(e) => {
                e.stopPropagation();
                onRemoveOpen();
              }}
              isDisabled={disableDelete}
            >
              <FormattedMessage defaultMessage="Remove" description="button label" />
            </MenuItem>
          </Tooltip>
        </MenuList>
      </Menu>
      <RenameBankAccountModal
        isOpen={isRenameOpen}
        onClose={onRenameClose}
        bankAccount={bankAccount}
        onComplete={onComplete}
      />
      <RemoveBankAccountModal
        isOpen={isRemoveOpen}
        onClose={onRemoveClose}
        bankAccount={bankAccount}
        onComplete={onComplete}
      />
    </Box>
  );
};

export default MoreOptionsMenu;
